import {Component, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {CodeTableEnum} from "../../../shared/model/code";
import {Store} from "@ngrx/store";
import {selectDealReturnSummaryForCard, selectDealReturnSummaryPreviewError, selectDealReturnSummaryTable, selectIsAnyDealFormInEditMode} from "../../store/deal/deal.selectors";
import {FormBuilder} from "@angular/forms";
import {map, Subscription} from "rxjs";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import Big from "big.js";
import {DealReturnSummaryActions} from "../../store/deal.actions";
import {MatDialog} from "@angular/material/dialog";
import {GoalSeekDialogComponent, GoalSeekDialogData} from "../../../shared/components/goal-seek-dialog/goal-seek-dialog.component";
import {Deal} from "../../models/deal";
import {TraceableMoney, TraceablePercent} from "../../../shared/model/traceable";
import {Router} from "@angular/router";

@Component({
    selector: "valumize-deal-return-summary",
    templateUrl: "./deal-return-summary.component.html",
    styleUrls: ["./deal-return-summary.component.scss"],
    standalone: false
})
export class DealReturnSummaryComponent implements OnInit, OnDestroy {

    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);

    subscriptions: Subscription[] = [];
    returnSummary$ = this.store.select(selectDealReturnSummaryTable);
    isEditDisabled$ = this.store.select(selectIsAnyDealFormInEditMode);
    previewError$ = this.store.select(selectDealReturnSummaryPreviewError);
    codeTableScenarioType = CodeTableEnum.SHARED_SCENARIOTYPE;

    @Input() readOnly = false;

    isEditable = false;
    deal: Deal | null = null;
    discountToNAV: TraceablePercent | undefined;
    bidPrice: TraceableMoney | undefined;

    dealReturnSummaryForm = this.formBuilder.group({
        bidPriceAdjustment: this.formBuilder.control<number | null>(null),
    });

    ngOnInit() {
        this.subscriptions.push(this.store.select(selectDealReturnSummaryForCard).pipe(map((card) => {
            this.isEditable = card.isEditable;
            this.deal = card.deal;
            this.discountToNAV = card.discountToNAV;
            this.bidPrice = card.bidPrice;

            if (!card.isEditable) {
                const bidPriceAdjustment = DecimalFormatPipe.transformFromMillionsNum(card.deal.bidPriceAdjustment?.amount ?? undefined) ?? null;
                this.dealReturnSummaryForm.patchValue({bidPriceAdjustment}, {emitEvent: false});
            } else if (!card.isLoading && card.goalSeekBidPriceAdjustment != null) {
                const bidPriceAdjustment = DecimalFormatPipe.transformFromMillionsNum(card.goalSeekBidPriceAdjustment.amount ?? undefined) ?? null;
                this.dealReturnSummaryForm.patchValue({bidPriceAdjustment}, {emitEvent: false});
            }
        })).subscribe());
    }

    /**
     * WI 1085: the Probability column shows the effective weighting actually applied across the deal
     * (resolved by the backend from the constituent fund valuations), not the deal-level triple which
     * no longer drives the weighted case. Each scenario row shows its component as a whole percent, or
     * "various" when the deal's contributing lines don't share one triple.
     */
    probabilityLabel(scenarioCode: string, effectiveWeighting: { low: number; base: number; high: number } | null): string {
        if (!effectiveWeighting) {
            return "various";
        }
        const percent = (fraction: number) => `${new Big(DecimalFormatPipe.transformFractionToPercent(fraction) ?? 0).round(0)}%`;
        switch (scenarioCode) {
            case "LOW":
                return percent(effectiveWeighting.low);
            case "BASE":
                return percent(effectiveWeighting.base);
            case "HIGH":
                return percent(effectiveWeighting.high);
            default:
                return "";
        }
    }

    onBidPriceAdjustmentChange() {
        const bidPriceAdjustment = DecimalFormatPipe.transformToMillionsNum(this.dealReturnSummaryForm.controls.bidPriceAdjustment.value ?? undefined);
        this.store.dispatch(DealReturnSummaryActions.calc({bidPriceAdjustment}));
    }

    save() {
        const bidPriceAdjustment = DecimalFormatPipe.transformToMillionsNum(this.dealReturnSummaryForm.controls.bidPriceAdjustment.value ?? undefined);
        this.store.dispatch(DealReturnSummaryActions.save({bidPriceAdjustment}));
    }

    openGoalSeekDialog() {
        const deal = this.deal;
        if (!deal) return;

        const data: GoalSeekDialogData = {
            mode: "deal",
            hasDeal: false,
            goalSeekTvpi: deal.goalSeekTvpi?.factor ?? undefined,
            goalSeekTvpiActive: deal.goalSeekTvpiActive?.value ?? false,
            goalSeekIrr: deal.goalSeekIrr?.fraction ?? undefined,
            goalSeekIrrActive: deal.goalSeekIrrActive?.value ?? false,
            goalSeekDiscount: deal.goalSeekDiscount?.fraction ?? undefined,
            goalSeekDiscountActive: deal.goalSeekDiscountActive?.value ?? false,
        };

        this.dialog.open(GoalSeekDialogComponent, {
            data,
            width: "500px",
            maxHeight: "90vh"
        });
    }

    openSensitivityPage() {
        const dealId = this.deal?.id;
        if (dealId == null) return;
        this.router.navigate(["/deals", dealId, "sensitivity"]);
    }

    editMode = () => this.store.dispatch(DealReturnSummaryActions.edit());

    cancel = () => this.store.dispatch(DealReturnSummaryActions.cancel());

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
